import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { GroupAudienceBuilder } from "@/components/outreach/GroupAudienceBuilder";
import { VoterGroupAudiencesLibrary } from "@/components/outreach/VoterGroupAudiencesLibrary";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/hooks/use-session";
import { useVoterGroupAudiences } from "@/hooks/use-voter-groups";

export const Route = createFileRoute("/_app/_/outreach/group-audiences")({
	component: GroupAudiencesPage,
	// `?build=new` opens the full-page builder for a new audience; `?build=<id>`
	// reopens it on a saved voter-group audience for editing. Kept in the URL so
	// refresh, back, and shared links land on the right surface.
	validateSearch: (
		search: Record<string, unknown>,
	): { build?: "new" | number } => {
		const raw = search.build;
		if (raw === "new") return { build: "new" };
		const id = Number(raw);
		return Number.isFinite(id) && id > 0 ? { build: id } : {};
	},
});

function GroupAudiencesPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const navigate = useNavigate({ from: Route.fullPath });
	const { build } = Route.useSearch();

	if (!campaignId) {
		return (
			<div className="mx-auto max-w-4xl p-6">
				<p className="text-muted-foreground">
					No campaign found. Please complete onboarding first.
				</p>
			</div>
		);
	}

	const openList = () => navigate({ search: {} });

	if (build === "new") {
		return (
			<GroupAudienceBuilder
				key="new"
				campaignId={campaignId}
				onDone={openList}
				onCancel={openList}
			/>
		);
	}

	if (typeof build === "number") {
		return (
			<EditAudience
				key={build}
				campaignId={campaignId}
				audienceId={build}
				onDone={openList}
				onCancel={openList}
			/>
		);
	}

	return (
		<VoterGroupAudiencesLibrary
			campaignId={campaignId}
			onNew={() => navigate({ search: { build: "new" } })}
			onEdit={(a) => navigate({ search: { build: a.id } })}
		/>
	);
}

/** Resolve the saved audience for `?build=<id>` before opening the builder on it. */
function EditAudience({
	campaignId,
	audienceId,
	onDone,
	onCancel,
}: {
	campaignId: number;
	audienceId: number;
	onDone: () => void;
	onCancel: () => void;
}) {
	const query = useVoterGroupAudiences(campaignId, true);
	if (query.isLoading) {
		return (
			<div className="mx-auto max-w-2xl space-y-4 p-6">
				<Skeleton className="h-28 w-full" />
				<Skeleton className="h-20 w-full" />
			</div>
		);
	}
	// A fetch failure is not the same as a missing record: show a retry, not the
	// "not found" branch, so a transient outage doesn't masquerade as a real 404.
	if (query.error) {
		return (
			<div className="mx-auto max-w-2xl space-y-3 p-6">
				<p className="text-sm text-destructive">
					Could not load this audience.
				</p>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => query.refetch()}>
						Retry
					</Button>
					<Button variant="ghost" size="sm" onClick={onCancel}>
						Back to audiences
					</Button>
				</div>
			</div>
		);
	}
	const audience = (query.data ?? []).find((a) => a.id === audienceId);
	if (!audience) {
		return (
			<div className="mx-auto max-w-2xl space-y-3 p-6">
				<p className="text-sm text-destructive">
					That audience could not be found.
				</p>
				<Button variant="outline" size="sm" onClick={onCancel}>
					Back to audiences
				</Button>
			</div>
		);
	}
	return (
		<GroupAudienceBuilder
			campaignId={campaignId}
			editAudience={audience}
			onDone={onDone}
			onCancel={onCancel}
		/>
	);
}
